import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
} from '@material-ui/core'

import {
  Clear,
  InsertDriveFileOutlined,
  CheckCircleOutline,
  ErrorOutline,
  CloudUploadOutlined,
  GetApp,
} from '@material-ui/icons'

import ReModal from '../../components/Modal/Modal'
import sample from '../assets/sample.xls'
import * as XLSX from 'xlsx'

import './NbrStyles/Style.scss'
import { style } from './NbrStyles/NBrStyle'

import { uploadAddTcin, resetState } from '../store/NationalBrand/ActionCreator'
import { CSVLink } from 'react-csv'
import { showNotification } from '../Notifications/NotificationActionCreator'

const serviceTypes = [
  {
    title: 'Ingest 3D Image',
    price: 'Free',
    details:
      "Upload your existing 3D image for quality checks and have it published onto Target.com at no extra charge. Will require edits if it does not meet Target's quality requirements.",
  },
  {
    title: 'Create 3D Image',
    price: '$50/Item',
    details:
      'Build a 3D image for the item. The 3D image will be Target owned and cannot be used externally.',
  },
  {
    title: 'Create & License 3D Image',
    price: '$70/Item',
    details:
      'Build a 3D image for the item and own the rights to download the 3D image for external use.',
  },
]

// The add_tcins API expects compact service type enum values.
// Keep the user-facing/template labels readable and normalize them before upload.
const SERVICE_TYPE_MAP = {
  ingest3dimage: 'Ingest3DImage',
  create3dimage: 'Create3DImage',
  'create&license3dimage': 'Create&License3DImage',
}

const normalizeServiceType = (value = '') => {
  const normalizedKey = String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase()

  return SERVICE_TYPE_MAP[normalizedKey] || ''
}

const NBrAddTcin = ({
  openAddTcin,
  setAddTcinModal,
  title,
  projectId,
  fetchList,
}) => {
  const dispatch = useDispatch()

  const { email } = useSelector((state) => state.auth)

  const { addTcin, nbrVendorConfirm } = useSelector(
    (state) => state.nationalBrand
  )

  const [file, setFile] = useState(null)
  const [loader, setLoader] = useState(false)
  const [showLink, setShowLink] = useState(false)
  const [tcinResponseList, setTcinResponseList] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [fileFeedback, setFileFeedback] = useState(null)

  useEffect(() => {
    if (addTcin && addTcin[0] === 'error') {
      setLoader(false)
      setShowLink(false)
      setTcinResponseList(null)
      setFileFeedback({
        type: 'error',
        title: 'The upload could not be completed.',
        message:
          'Your file was read, but the TCIN upload service returned an error. This is not necessarily a filename problem.',
        fix: 'Keep the modal open and try the file again. If it repeats, verify the TCIN, Service Type and Input Type values or download a fresh template.',
      })

      dispatch(
        showNotification(
          true,
          'Upload could not be completed. Review the message in the Add TCIN window and try again.',
          'error'
        )
      )
    }

    if (
      addTcin &&
      addTcin[0] !== 'error' &&
      addTcin.tcin_list &&
      addTcin.tcin_list.length > 0
    ) {
      setLoader(false)
      setShowLink(true)

      let failTcinCount = 0
      let successTcinCount = 0

      addTcin.tcin_list.forEach((item) => {
        if (item.verification_status === 'FAILURE') {
          failTcinCount += 1
        }

        if (item.verification_status === 'SUCCESS') {
          successTcinCount += 1
        }
      })

      setTcinResponseList({
        fail: failTcinCount,
        Success: successTcinCount,
      })

      if (successTcinCount > 0 && failTcinCount === 0) {
        setFileFeedback({
          type: 'success',
          title: 'Upload complete.',
          message: `${successTcinCount} TCIN${
            successTcinCount === 1 ? '' : 's'
          } successfully added.`,
        })
      } else if (successTcinCount > 0 && failTcinCount > 0) {
        setFileFeedback({
          type: 'warning',
          title: 'Upload completed with some issues.',
          message: `${successTcinCount} TCIN${
            successTcinCount === 1 ? '' : 's'
          } added and ${failTcinCount} failed.`,
          fix: 'Download the failure report, review the failed TCINs, correct those rows, and upload them again.',
        })
      } else if (failTcinCount > 0) {
        setFileFeedback({
          type: 'error',
          title: 'No TCINs were added.',
          message: `${failTcinCount} TCIN${
            failTcinCount === 1 ? '' : 's'
          } failed verification.`,
          fix: 'Download the failure report, correct the failed rows in the template, save the workbook, and upload it again.',
        })
      }
    }

    // eslint-disable-next-line
  }, [addTcin, showLink])

  const getFileExtension = (fileName = '') => {
    const parts = fileName.trim().split('.')
    return parts.length > 1 ? parts.pop().toLowerCase() : ''
  }

  const getSuggestedSafeName = (fileName = '') => {
    const extension = getFileExtension(fileName)
    const baseName = extension
      ? fileName.slice(0, -(extension.length + 1))
      : fileName

    const safeBaseName = baseName
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^a-zA-Z0-9 _().-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/[. ]+$/g, '')

    return extension
      ? `${safeBaseName || 'TCIN_Template'}.${extension}`
      : `${safeBaseName || 'TCIN_Template'}.xlsx`
  }

  const setFileIssue = (titleText, message, fix, suggestedName) => {
    setLoader(false)
    setShowLink(false)
    setTcinResponseList(null)
    setFileFeedback({
      type: 'error',
      title: titleText,
      message,
      fix,
      suggestedName,
    })

    dispatch(showNotification(true, titleText, 'error'))
  }

  const validateFileBeforeRead = (fileObj) => {
    const originalName = (fileObj?.name || '').trim()

    if (!originalName) {
      return {
        valid: false,
        title: 'File name is missing.',
        message: 'We could not identify the selected file name.',
        fix: 'Choose the Excel file again from your computer.',
      }
    }

    if (fileObj.size === 0) {
      return {
        valid: false,
        title: 'The selected file is empty.',
        message: `"${originalName}" has no file content.`,
        fix: 'Open the workbook in Excel, save it again, then select the newly saved .xls or .xlsx file.',
      }
    }

    // Excel creates temporary lock files such as ~$template.xlsx.
    if (originalName.startsWith('~$')) {
      return {
        valid: false,
        title: 'This is a temporary Excel file.',
        message: `"${originalName}" is an Excel lock/temporary file, not the actual workbook.`,
        fix: 'Close the workbook in Excel, then select the original file that does not start with "~$".',
      }
    }

    const extension = getFileExtension(originalName)

    if (!extension) {
      return {
        valid: false,
        title: 'The file has no Excel extension.',
        message: `"${originalName}" does not end in .xls or .xlsx.`,
        fix: 'Open the file in Excel and use Save As > Excel Workbook (.xlsx), then upload the saved file.',
        suggestedName: getSuggestedSafeName(originalName),
      }
    }

    if (extension !== 'xls' && extension !== 'xlsx') {
      return {
        valid: false,
        title: 'Unsupported file type.',
        message: `"${originalName}" is a .${extension} file. This upload accepts only .xls or .xlsx workbooks.`,
        fix: 'Open the source file in Excel and use Save As > Excel Workbook (.xlsx). Do not only rename the extension.',
      }
    }

    // Spaces, underscores, hyphens and copy suffixes such as (1) are valid.
    // We only suggest a safer name when unusual characters are present.
    const safeName = getSuggestedSafeName(originalName)
    const hasUnusualCharacters = safeName !== originalName

    return {
      valid: true,
      safeName,
      hasUnusualCharacters,
    }
  }

  const normalizeTemplateHeader = (value = '') =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')

  const validateTemplateHeaders = (rows) => {
    const headerRow = Array.isArray(rows?.[0]) ? rows[0] : []

    // Normalize headers so formatting differences such as spaces, underscores,
    // capitalization, and the template's "3D Service type" label are accepted.
    const actual = {
      tcin: normalizeTemplateHeader(headerRow[0]),
      serviceType: normalizeTemplateHeader(headerRow[1]),
      inputType: normalizeTemplateHeader(headerRow[2]),
    }

    const invalidHeaders = []

    if (actual.tcin !== 'tcin') invalidHeaders.push('TCIN')

    // The downloaded template uses "3D Service type". Keep accepting
    // "Service Type" as well for backward compatibility with older files.
    const validServiceTypeHeaders = ['3dservicetype', 'servicetype']

    if (!validServiceTypeHeaders.includes(actual.serviceType)) {
      invalidHeaders.push('3D Service type')
    }

    if (actual.inputType !== 'inputtype') invalidHeaders.push('Input type')

    return {
      valid: invalidHeaders.length === 0,
      invalidHeaders,
      foundHeaders: headerRow
        .slice(0, 3)
        .map((header) => String(header ?? '').trim() || '(blank)'),
    }
  }

  const processExcelRows = (rows, fileObj) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      setFileIssue(
        'The workbook is empty.',
        `"${fileObj.name}" opened successfully, but the first sheet has no data.`,
        'Download a fresh template, enter the TCIN rows, save it, and upload it again.'
      )
      return
    }

    // Block files that are valid Excel workbooks but are not the TCIN template.
    // This prevents unrelated spreadsheets from being interpreted as
    // TCIN / Service Type / Input Type data just because columns A-C have values.
    const headerValidation = validateTemplateHeaders(rows)

    if (!headerValidation.valid) {
      const found = headerValidation.foundHeaders.join(' | ')

      setFileIssue(
        'This file does not match the TCIN template.',
        `Required columns are TCIN, 3D Service type and Input type. Missing or incorrect column(s): ${headerValidation.invalidHeaders.join(
          ', '
        )}. Found in the first three columns: ${found}.`,
        'Download a fresh template and keep the first row as TCIN, 3D Service type and Input type. Copy your TCIN data into those columns, save the workbook, and upload it again.'
      )
      return
    }

    // First row is the template header. Remaining rows are data rows.
    const dataRows = rows.slice(1)
    const result = []
    const rowErrors = []

    dataRows.forEach((row, index) => {
      const excelRowNumber = index + 2
      const tcin = String(row?.[0] ?? '')
        .trim()
        .replace(/\s/g, '')
      const serviceTypeInput = String(row?.[1] ?? '').trim()
      const serviceType = normalizeServiceType(serviceTypeInput)
      const inputType = String(row?.[2] ?? '').trim()

      // Ignore completely empty rows.
      if (!tcin && !serviceTypeInput && !inputType) return

      if (!tcin) {
        rowErrors.push(`Row ${excelRowNumber}: TCIN is missing`)
        return
      }

      if (!/^\d+$/.test(tcin)) {
        rowErrors.push(`Row ${excelRowNumber}: TCIN must contain digits only`)
        return
      }

      if (!serviceTypeInput) {
        rowErrors.push(`Row ${excelRowNumber}: Service Type is missing`)
        return
      }

      if (!serviceType) {
        rowErrors.push(
          `Row ${excelRowNumber}: Service Type "${serviceTypeInput}" is not valid. ` +
            'Use Ingest 3D Image, Create 3D Image, or Create & License 3D Image'
        )
        return
      }

      if (!inputType) {
        rowErrors.push(`Row ${excelRowNumber}: Input Type is missing`)
        return
      }

      result.push({
        tcin,
        service_type: serviceType,
        input_type: inputType,
      })
    })

    if (rowErrors.length > 0) {
      const firstErrors = rowErrors.slice(0, 3).join('; ')
      const remaining = rowErrors.length - 3
      const extraText = remaining > 0 ? `; and ${remaining} more issue(s)` : ''

      setFileIssue(
        'The filename is valid, but some template rows need attention.',
        `${firstErrors}${extraText}.`,
        'Correct the listed rows in the downloaded template, save the workbook, and upload the same file again.'
      )
      return
    }

    if (result.length === 0) {
      setFileIssue(
        'No TCIN rows were found.',
        `"${fileObj.name}" is a valid Excel file, but there are no completed rows below the header.`,
        'Add at least one TCIN row with Service Type and Input Type, save the workbook, and upload it again.'
      )
      return
    }

    setFileFeedback({
      type: 'success',
      title: 'File accepted.',
      message: `${fileObj.name} contains ${result.length} valid TCIN row${
        result.length === 1 ? '' : 's'
      }. Uploading now.`,
    })

    dispatch(
      uploadAddTcin(projectId, email, nbrVendorConfirm.vendor_id, result)
    )
  }

  const processFile = useCallback(
    (fileObj) => {
      if (!fileObj) {
        setFileIssue(
          'No file was selected.',
          'We did not receive a file from the file picker.',
          'Choose an .xls or .xlsx file and try again.'
        )
        return
      }

      const validation = validateFileBeforeRead(fileObj)

      if (!validation.valid) {
        setFile(fileObj)
        setFileIssue(
          validation.title,
          validation.message,
          validation.fix,
          validation.suggestedName
        )
        return
      }

      setLoader(true)
      setFile(fileObj)
      setShowLink(false)
      setTcinResponseList({})

      if (validation.hasUnusualCharacters) {
        setFileFeedback({
          type: 'warning',
          title: 'The Excel filename is accepted.',
          message:
            'Spaces, underscores and (1) are okay. This filename also contains unusual characters, which may be less portable across systems.',
          fix: 'You can continue, or rename the file before uploading again if you want maximum compatibility.',
          suggestedName: validation.safeName,
        })
      } else {
        setFileFeedback({
          type: 'success',
          title: 'Filename accepted.',
          message:
            'Spaces, underscores, hyphens and copy suffixes such as (1) are supported. Checking the workbook contents now.',
        })
      }

      const reader = new FileReader()

      reader.onload = (evt) => {
        try {
          const arrayBuffer = evt.target.result
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            setFileIssue(
              'The Excel workbook has no worksheet.',
              `"${fileObj.name}" could be opened, but no worksheet was found.`,
              'Open the file in Excel, make sure the template sheet exists, save it, and upload it again.'
            )
            return
          }

          const sheetName = workbook.SheetNames[0]
          const sheet = workbook.Sheets[sheetName]

          const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: '',
            raw: false,
          })

          processExcelRows(rows, fileObj)
        } catch (error) {
          console.error('Unable to read Excel file:', error)

          setFileIssue(
            'The filename is valid, but the workbook could not be read.',
            `"${fileObj.name}" has a supported .xls/.xlsx name, so the problem is likely inside the file rather than its name.`,
            'Open the file in Excel and use Save As > Excel Workbook (.xlsx). If it still fails, download a fresh template and copy only the TCIN data into it.'
          )
        }
      }

      reader.onerror = () => {
        setFileIssue(
          'The selected file could not be opened.',
          `The browser could not read "${fileObj.name}".`,
          'Make sure the file is available locally, then choose it again. If it is open in another app, close it and retry.'
        )
      }

      reader.readAsArrayBuffer(fileObj)
    },
    // eslint-disable-next-line
    [projectId, email, nbrVendorConfirm]
  )

  const handleUpload = (e) => {
    e.preventDefault()

    const selectedFile = e.target.files && e.target.files[0]

    if (selectedFile) {
      processFile(selectedFile)
    } else {
      setFileIssue(
        'No file was selected.',
        'The file picker was closed without choosing a workbook.',
        'Choose an .xls or .xlsx file when you are ready.'
      )
    }

    // Allows the user to select the same file again after fixing it.
    e.target.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)

    const droppedFiles = e.dataTransfer.files

    if (!droppedFiles || droppedFiles.length === 0) {
      setFileIssue(
        'No file was dropped.',
        'We did not receive a file from the drop area.',
        'Drag one .xls or .xlsx workbook into the upload area.'
      )
      return
    }

    if (droppedFiles.length > 1) {
      setFileIssue(
        'Only one workbook can be uploaded at a time.',
        `${droppedFiles.length} files were dropped.`,
        'Drop only the completed TCIN template you want to upload.'
      )
      return
    }

    processFile(droppedFiles[0])
  }

  const handleClickProceed = () => {
    if (
      tcinResponseList !== null &&
      tcinResponseList['fail'] === 0 &&
      tcinResponseList['Success'] > 0
    ) {
      dispatch(
        showNotification(true, 'All Tcin Successfully Uploaded', 'success')
      )
    }

    if (
      tcinResponseList !== null &&
      tcinResponseList['fail'] > 0 &&
      tcinResponseList['Success'] > 0
    ) {
      dispatch(
        showNotification(
          true,
          `Tcin Successfully Added : ${tcinResponseList['Success']} and Failed to Add : ${tcinResponseList['fail']}`,
          'success'
        )
      )
    }

    setAddTcinModal(false)
    setFile(null)
    setShowLink(false)

    dispatch(resetState('addTcin'))

    fetchList()

    setTcinResponseList(null)
    setFileFeedback(null)
  }

  const conditionForCSV =
    addTcin &&
    !loader &&
    showLink &&
    addTcin[0] !== 'error' &&
    addTcin.tcin_list &&
    addTcin.tcin_list.length > 0

  const handleCloseAddTcin = () => {
    if (loader) {
      setAddTcinModal(true)
    } else {
      dispatch(resetState('addTcin'))

      setAddTcinModal(false)
      setShowLink(false)
      setFile(null)
    }

    if (
      tcinResponseList !== null &&
      tcinResponseList['fail'] > 0 &&
      tcinResponseList['Success'] === 0
    ) {
      dispatch(
        showNotification(
          true,
          `Failed To Add Tcin : ${tcinResponseList['fail']}`,
          'error'
        )
      )
    }

    setTcinResponseList(null)
    setFileFeedback(null)
  }

  return (
    openAddTcin && (
      <ReModal open={openAddTcin}>
        {/*
          IMPORTANT:
          Material UI v4 does not support sx={}
          so use style={style}
        */}
        <Box
          style={{
            ...style,
            width: '900px',
            maxWidth: '92vw',
            padding: 0,
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
          className="Add-Tcin-modal"
        >
          {/* HEADER */}
          <div
            className="main-contain"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid #e6e6e6',
            }}
          >
            <Typography
              style={{
                fontSize: 18,
                fontWeight: 500,
                lineHeight: '24px',
              }}
            >
              Add TCIN(s) to project{' '}
              <span style={{ fontWeight: 400 }}>({title})</span>
            </Typography>

            <Tooltip
              title={loader ? 'Upload in progress — please wait' : ''}
              placement="left"
            >
              <span>
                <IconButton
                  onClick={handleCloseAddTcin}
                  disabled={loader}
                  size="small"
                  style={{
                    padding: 4,
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>

          {/* BODY */}
          <Grid
            container
            style={{
              minHeight: 440,
            }}
          >
            {/* LEFT SECTION */}
            <Grid
              item
              xs={6}
              style={{
                padding: '22px 24px',
                borderRight: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
            >
              {/* STEP 1 */}
              <div
                style={{
                  marginBottom: 24,
                }}
              >
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#555',
                    letterSpacing: '0.5px',
                    marginBottom: 7,
                  }}
                >
                  STEP 1
                </Typography>

                <Typography
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    color: '#333',
                    marginBottom: 10,
                  }}
                >
                  Download the template, fill in TCINs with the desired service
                  type and input type for each row.
                </Typography>

                <a
                  href={sample}
                  download="template.xls"
                  className="sample-link"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  <GetApp
                    fontSize="small"
                    style={{
                      marginRight: 5,
                    }}
                  />
                  Download Template.xls
                </a>
              </div>

              {/* STEP 2 */}
              <div>
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#555',
                    letterSpacing: '0.5px',
                    marginBottom: 7,
                  }}
                >
                  STEP 2
                </Typography>

                <Typography
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    color: '#333',
                  }}
                >
                  Upload your completed template file (.xlsx / .xls).
                </Typography>

                {/* DROP AREA */}
                <form
                  method="POST"
                  id="uploadForm"
                  name="theUploadForm"
                  encType="multipart/form-data"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className="upload-Button"
                  style={{
                    marginTop: 12,
                  }}
                >
                  <input
                    type="file"
                    name="fileInput"
                    id="fileInput"
                    onChange={handleUpload}
                    hidden
                    accept=".xlsx,.xls"
                  />

                  <label
                    htmlFor="fileInput"
                    style={{
                      border: isDragOver
                        ? '2px dashed #cc0000'
                        : '1px dashed #8b8f98',

                      backgroundColor: isDragOver ? '#fff6f6' : '#fff',

                      minHeight: 100,
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    <CloudUploadOutlined
                      style={{
                        marginBottom: 5,
                      }}
                    />

                    <span>
                      {file ? 'Replace file' : 'Click to upload or drag & drop'}
                    </span>

                    <p>.xls or .xlsx — max one file</p>

                    <p
                      style={{
                        marginTop: 2,
                        fontSize: 11,
                        fontWeight: 400,
                      }}
                    >
                      Spaces, underscores and names like (1) are supported
                    </p>
                  </label>
                </form>

                {/* USER-FRIENDLY FILE VALIDATION / FIX MESSAGE */}
                {fileFeedback && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      borderRadius: 4,
                      border:
                        fileFeedback.type === 'error'
                          ? '1px solid rgba(204, 0, 0, 0.25)'
                          : fileFeedback.type === 'warning'
                          ? '1px solid rgba(199, 119, 0, 0.30)'
                          : '1px solid rgba(59, 135, 62, 0.25)',
                      background:
                        fileFeedback.type === 'error'
                          ? 'rgba(204, 0, 0, 0.05)'
                          : fileFeedback.type === 'warning'
                          ? 'rgba(237, 108, 2, 0.06)'
                          : 'rgba(76, 175, 80, 0.06)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                      }}
                    >
                      {fileFeedback.type === 'success' ? (
                        <CheckCircleOutline
                          fontSize="small"
                          style={{ color: '#3b873e', marginTop: 1 }}
                        />
                      ) : (
                        <ErrorOutline
                          fontSize="small"
                          style={{
                            color:
                              fileFeedback.type === 'warning'
                                ? '#c77700'
                                : '#cc0000',
                            marginTop: 1,
                          }}
                        />
                      )}

                      <div style={{ minWidth: 0 }}>
                        <Typography
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            lineHeight: '18px',
                            color: '#333',
                          }}
                        >
                          {fileFeedback.title}
                        </Typography>

                        {fileFeedback.message && (
                          <Typography
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              lineHeight: '17px',
                              color: '#555',
                            }}
                          >
                            {fileFeedback.message}
                          </Typography>
                        )}

                        {fileFeedback.type === 'error' && fileFeedback.fix && (
                          <Typography
                            style={{
                              marginTop: 5,
                              fontSize: 12,
                              lineHeight: '17px',
                              color: '#333',
                            }}
                          >
                            <strong>How to fix:</strong> {fileFeedback.fix}
                          </Typography>
                        )}

                        {fileFeedback.type === 'warning' && fileFeedback.fix && (
                          <Typography
                            style={{
                              marginTop: 5,
                              fontSize: 12,
                              lineHeight: '17px',
                              color: '#333',
                            }}
                          >
                            <strong>Suggestion:</strong> {fileFeedback.fix}
                          </Typography>
                        )}

                        {fileFeedback.type !== 'success' &&
                          fileFeedback.suggestedName && (
                            <Typography
                              style={{
                                marginTop: 5,
                                fontSize: 12,
                                lineHeight: '17px',
                                color: '#333',
                                wordBreak: 'break-word',
                              }}
                            >
                              <strong>Suggested filename:</strong>{' '}
                              {fileFeedback.suggestedName}
                            </Typography>
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {/* FILE STATUS */}
                {file && file.name && (
                  <div
                    className="upload-file-Names"
                    style={{
                      padding: '0 12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                      }}
                    >
                      <InsertDriveFileOutlined
                        fontSize="small"
                        style={{
                          marginRight: 8,
                          flexShrink: 0,
                        }}
                      />

                      <span
                        style={{
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </span>
                    </div>

                    <div
                      className="upload-with-icon"
                      style={{
                        alignItems: 'center',
                      }}
                    >
                      {loader && <CircularProgress size={18} />}

                      {conditionForCSV && (
                        <span
                          className="upload-complete"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <CheckCircleOutline fontSize="small" />
                          Upload complete
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* RESULT */}
                {conditionForCSV && tcinResponseList !== null && (
                  <div
                    className="tcin-list"
                    style={{
                      alignItems: 'center',
                    }}
                  >
                    <span
                      className="success"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <CheckCircleOutline fontSize="small" />
                      {tcinResponseList['Success']} added
                    </span>

                    {tcinResponseList['fail'] > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <span
                          className="fail"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <ErrorOutline fontSize="small" />
                          {tcinResponseList['fail']} failed
                        </span>

                        <CSVLink
                          data={
                            addTcin.tcin_list.length > 0
                              ? addTcin.tcin_list
                              : ''
                          }
                          target="_blank"
                          filename={`${title}_Tcin_Report.csv`}
                          className="sample-link"
                        >
                          Download failure report
                        </CSVLink>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* PROCEED */}
              <div
                style={{
                  marginTop: 'auto',
                  paddingTop: 20,
                }}
              >
                <Tooltip
                  title={
                    !tcinResponseList || !tcinResponseList['Success']
                      ? 'Upload a valid file to continue'
                      : ''
                  }
                  placement="top"
                >
                  <span
                    style={{
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    <Button
                      className={
                        addTcin?.tcin_list?.length > 0 &&
                        tcinResponseList?.['Success'] > 0
                          ? 'btn-upload btn-active'
                          : 'btn-upload'
                      }
                      style={{
                        position: 'static',
                        width: '100%',
                        minWidth: '100%',
                        height: 40,
                        marginTop: 0,
                        textTransform: 'none',
                      }}
                      disabled={
                        !(
                          tcinResponseList !== null &&
                          Object.prototype.hasOwnProperty.call(
                            tcinResponseList,
                            'Success'
                          ) &&
                          tcinResponseList['Success'] > 0
                        )
                      }
                      onClick={handleClickProceed}
                    >
                      Proceed
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </Grid>

            {/* RIGHT SECTION */}
            <Grid
              item
              xs={6}
              style={{
                padding: '22px 24px',
                background: '#fafafa',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  marginBottom: 4,
                  color: '#222',
                }}
              >
                3D Services Offered by Target Plus
              </Typography>

              <Typography
                style={{
                  fontSize: 13,
                  color: '#666',
                  marginBottom: 16,
                }}
              >
                Choose the right service for each TCIN
              </Typography>

              {serviceTypes.map((item) => {
                const isFree = item.price === 'Free'

                return (
                  <div
                    key={item.title}
                    className="service-type-box"
                    style={{
                      width: '100%',
                      height: 'auto',
                      margin: '0 0 12px 0',
                      padding: '14px 16px',
                      background: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: 6,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      className="service-type-details"
                      style={{
                        gap: 12,
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#222',
                        }}
                      >
                        {item.title}
                      </Typography>

                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '4px 9px',
                          borderRadius: 12,

                          background: isFree ? '#e8f5e9' : '#f0f0f0',

                          color: isFree ? '#2e7d32' : '#444',

                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.price}
                      </span>
                    </div>

                    <Typography
                      style={{
                        fontSize: 13,
                        lineHeight: '19px',
                        color: '#555',
                        marginTop: 8,
                      }}
                    >
                      {item.details}
                    </Typography>
                  </div>
                )
              })}
            </Grid>
          </Grid>
        </Box>
      </ReModal>
    )
  )
}

export default NBrAddTcin
